import { IntegrationReportRules } from '@app/model/integration-settings/Integration';

// ROO2-315 B6/B10, ROO2-309 Q1: the words of the report rules, shared by the Report rules tab and the BI captions, so the
// card and the reports always say the same thing. A property with no rules (5stelle) or with every default keeps today's
// texts word for word (315 Q5).

export const DEFAULT_REPORT_RULES: IntegrationReportRules = {
	outOfOrderReducesRooms: false,
	dayUseOccupied: false,
	noShowOccupied: false,
	houseUseOccupied: true,
	roomRevenue: 'STAY_NIGHTS',
};

const occupancyIsDefault = (r: IntegrationReportRules): boolean =>
	!r.outOfOrderReducesRooms && !r.dayUseOccupied && !r.noShowOccupied && r.houseUseOccupied;

export const isDefaultRules = (r: IntegrationReportRules | null): boolean =>
	r === null || (occupancyIsDefault(r) && r.roomRevenue === 'STAY_NIGHTS');

// "Room nights + day-use rooms − house-use rooms"
export const occupiedWords = (r: IntegrationReportRules): string =>
	['Room nights', r.dayUseOccupied ? ' + day-use rooms' : '', r.noShowOccupied ? ' + no-show rooms' : '',
		r.houseUseOccupied ? '' : ' − house-use rooms'].join('');

// "rooms − out-of-order rooms"
export const availableWords = (r: IntegrationReportRules, rooms: number | null): string =>
	`${rooms ?? '–'} rooms${r.outOfOrderReducesRooms ? ' − out-of-order rooms of the night' : ''}`;

export const revenueWords = (r: IntegrationReportRules): string =>
	r.roomRevenue === 'ALL_ROOM_POSTINGS' ? 'Room revenue of all room postings' : 'Room revenue';

export type RuleCaptions = { occupancy: string, adr: string, revpar: string, note: string | null };

// The info texts of Occupancy, ADR and RevPAR. rules null = no report rules (today's texts).
export const ruleCaptions = (rules: IntegrationReportRules | null, rooms: number | null): RuleCaptions => {
	const r = rules ?? DEFAULT_REPORT_RULES;
	if (isDefaultRules(rules)) return {
		occupancy: `Room nights ÷ ${rooms ?? '–'} rooms (the sum of the known room counts).`,
		adr: 'Room revenue ÷ room nights.',
		revpar: `Room revenue ÷ ${rooms ?? '–'} rooms.`,
		note: null,
	};
	const occupied = occupancyIsDefault(r) ? 'room nights' : `occupied rooms (${occupiedWords(r).toLowerCase()})`;
	return {
		occupancy: `Occupied rooms (${occupiedWords(r).toLowerCase()}) ÷ available rooms (${availableWords(r, rooms)}).`,
		adr: `${revenueWords(r)} ÷ ${occupied}.`,
		revpar: `${revenueWords(r)} ÷ available rooms (${availableWords(r, rooms)}).`,
		note: `Report rules of this property (Settings › Integrations › Reservations): ${ruleList(r).join('; ')}.`,
	};
};

// ROO2-315 part c1 (Q4): the note of a report of one or more properties (Segmentation, Room Types): the note of Daily
// Performance for one property; "these properties" when several share the rules; one sentence when their rules differ.
// null when no property of the report has rules that change a figure.
export const scopeRulesNote = (rules: IntegrationReportRules | null, differ: boolean, properties: number): string | null => {
	if (differ) return 'Each property counts by its own report rules (Settings › Integrations › Reservations).';
	const note = ruleCaptions(rules, null).note;
	return note === null || properties <= 1 ? note : note.replace('Report rules of this property', 'Report rules of these properties');
};

// ROO2-315 part c2 (Q4): the caption line of Rate plans, Guest Mix and Channels for a selection with rules (the words of the
// Excel notes, ReportRuleWords.withAdrRules); null without rules.
export const adrRulesNote = (rulesNote: string | null): string | null => rulesNote === null ? null : `ADR = REV ÷ occupied rooms. ${rulesNote}`;

// The rules that differ from today's rule, in words: for the note under the table and the Excel note.
export const ruleList = (r: IntegrationReportRules): string[] => [
	r.outOfOrderReducesRooms ? 'out-of-order rooms left out of the available rooms' : '',
	r.dayUseOccupied ? 'day use counted as occupied' : '',
	r.noShowOccupied ? 'no-shows counted as occupied' : '',
	r.houseUseOccupied ? '' : 'house use not counted as occupied',
	r.roomRevenue === 'ALL_ROOM_POSTINGS' ? 'Room revenue of all room postings, as the PMS report' : '',
].filter(s => s !== '');
